import React from "react";
import classnames from "classnames";

import { InputElementUnion } from "@graffer-inc/form-schema";

import { BaseModal } from "../../atoms/base-modal";
import { matchType } from "../../../application/domain/base/base-type";
import { SmartApplyR } from "../../pages/smart-apply/route";

type OperationState = "CONFIRM" | "PROCESSING";

type CsvType = {
  name: string;
  data: string;
};

const getLabelExamples = (inputElements: InputElementUnion[]) => {
  const etcStr = inputElements.length > 2 ? "など" : "";
  return inputElements
    .slice(0, 2)
    .map((e) => `「${e.label.text}」`)
    .join("、")
    .concat(etcStr);
};

export const BulkUpdateTenantProcessResultModal: React.FC<{
  isActive: boolean;
  onClose: () => void;
  onExecute: (csv: CsvType) => Promise<string | undefined>;
  inputElements: InputElementUnion[];
}> = ({ isActive, onClose, onExecute, inputElements }) => {
  const [csv, setCsv] = React.useState<CsvType | undefined>();
  const [pageStatus, setPageStatus] = React.useState<OperationState>("CONFIRM");
  const [taskId, setTaskId] = React.useState<string>();

  return (
    <BaseModal title="管理情報の一括入力" isActive={isActive} onClose={onClose}>
      {matchType(pageStatus, {
        CONFIRM: (
          <>
            <section className="modal-card-body">
              <div className="file has-name">
                <label className="file-label">
                  <input
                    className="file-input"
                    type="file"
                    accept="text/csv"
                    onChange={(e) => {
                      const fileReader = new FileReader();
                      const fileDropped = e.target.files![0];
                      if (!fileDropped) {
                        return;
                      }
                      fileReader.onload = (event) => {
                        const dataUri = event.target?.result as string;
                        setCsv({
                          name: fileDropped.name,
                          data: dataUri.split(",")[1],
                        });
                      };
                      fileReader.readAsDataURL(fileDropped);
                    }}
                  />
                  <span className="file-cta">
                    <span className="file-icon">
                      <i className="fas fa-upload" />
                    </span>
                    <span className="file-label">
                      {csv ? csv.name : "ファイルを選択…"}
                    </span>
                  </span>
                  <span className="file-name">
                    {csv ? "選択済み" : "未選択"}
                  </span>
                </label>
              </div>
              <div className="mt-4">
                <p>
                  管理情報が一括で入力できます。以下の例のように「申請ID」の他に管理情報（
                  {`${getLabelExamples(inputElements)}`}
                  ）が含まれているCSV形式のファイルをアップロードしてください。
                </p>
                <div className="table-container mt-4">
                  <table className="table is-bordered is-striped is-narrow">
                    <thead>
                      <tr>
                        <th>申請ID</th>
                        {inputElements.map((e) => (
                          <th
                            style={{
                              whiteSpace: "nowrap",
                            }}
                            key={e.id}
                          >
                            {e.label.text}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>0000-1111-2222-3333333</td>
                        {inputElements.map((e) => (
                          <th key={e.id}>...</th>
                        ))}
                      </tr>
                      <tr>
                        <th>...</th>
                        {inputElements.map((e) => (
                          <th key={e.id}>...</th>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="has-text-grey">
                  「申請ID」、「管理情報」以外の列は含まないでください。管理情報を更新しても申請者に通知されることはありません。
                </p>
              </div>
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button
                className={classnames("button is-info is-fullwidth")}
                onClick={async () => {
                  const id = await onExecute(csv!);
                  if (typeof id === "string") {
                    setTaskId(id);
                    setPageStatus("PROCESSING");
                    window.open(
                      `${SmartApplyR.ApplicationTaskDetail.link({ id })}`,
                      "_blank",
                    );
                  }
                }}
                disabled={!csv}
              >
                管理情報の一括入力を開始する
              </button>
            </footer>
          </>
        ),
        PROCESSING: taskId && (
          <>
            <section className="modal-card-body">
              管理情報の一括入力を開始しました。処理状況は
              <a
                href={SmartApplyR.ApplicationTaskDetail.link({
                  id: taskId,
                })}
                target="_blank"
                rel="noopener noreferrer"
              >
                こちら
                <i className="fas fa-external-link-alt"></i>
              </a>
              から確認してください。
            </section>
            <footer className="modal-card-foot">
              <button
                className={classnames("button is-fullwidth")}
                onClick={onClose}
              >
                閉じる
              </button>
            </footer>
          </>
        ),
      })}
    </BaseModal>
  );
};
